import React, { useCallback, useState } from 'react';

// Admin → Compute usage: share of the universe database's query compute by
// team, user and route (backend/services/computeUsage.js). Collapsed until
// opened — the numbers come from a live read of the query log.

interface Usage {
  queries: number; failed: number; cpuSeconds: number; durationSeconds: number;
  readRows: number; readGB: number; share: number;
}
interface UserRow extends Usage {
  userId: number; email: string; name: string | null; teamId: number | null; teamName: string | null;
  topRoutes: Array<Usage & { route: string }>;
}
interface TeamRow extends Usage { teamId: number | null; teamName: string | null; members: number[] }
interface Report {
  days: number; oldestLogDay: string | null; allReplicas: boolean; note: string | null;
  total: Usage; users: UserRow[]; teams: TeamRow[]; sources: Array<Usage & { source: string }>;
}

const n = (x: number) => x.toLocaleString();
const cpu = (s: number) => (s >= 3600 ? `${(s / 3600).toFixed(1)} h` : s >= 60 ? `${(s / 60).toFixed(1)} min` : `${s.toFixed(1)} s`);

const Bar: React.FC<{ share: number }> = ({ share }) => (
  <div className="flex items-center gap-2">
    <div className="w-24 h-2 bg-gray-100 rounded">
      <div className="h-2 bg-indigo-500 rounded" style={{ width: `${Math.min(100, share)}%` }} />
    </div>
    <span className="tabular-nums text-xs text-gray-700 w-12 text-right">{share.toFixed(1)}%</span>
  </div>
);

export const ComputeUsagePanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(7);
  const [view, setView] = useState<'teams' | 'users' | 'sources'>('teams');
  const [data, setData] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = useCallback(async (d: number) => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`/api/admin/compute-usage?days=${d}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Failed to load');
      setData(j);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggle = () => { const next = !open; setOpen(next); if (next && !data) load(days); };
  const pickDays = (d: number) => { setDays(d); load(d); };

  const th = 'px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide';
  const td = 'px-3 py-2 text-sm text-gray-800 tabular-nums';
  const numCells = (u: Usage) => (
    <>
      <td className={td}><Bar share={u.share} /></td>
      <td className={td}>{cpu(u.cpuSeconds)}</td>
      <td className={td}>{n(u.queries)}{u.failed ? <span className="text-red-600"> ({n(u.failed)} failed)</span> : null}</td>
      <td className={td}>{u.readGB.toFixed(2)} GB</td>
    </>
  );
  const numHeads = (
    <>
      <th className={th}>Share of compute</th><th className={th}>CPU time</th>
      <th className={th}>Queries</th><th className={th}>Data read</th>
    </>
  );

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <button onClick={toggle} className="w-full p-6 flex justify-between items-center text-left">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Compute usage</h2>
          <p className="text-sm text-gray-600">Who is using the search database — by team, user and feature</p>
        </div>
        <span className="text-gray-500 text-sm">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="px-6 pb-6">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {[1, 7, 30].map((d) => (
              <button key={d} onClick={() => pickDays(d)}
                className={`px-3 py-1 rounded-md text-sm border ${days === d ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                {d === 1 ? 'Last 24 hours' : `Last ${d} days`}
              </button>
            ))}
            <span className="mx-2 h-5 w-px bg-gray-200" />
            {(['teams', 'users', 'sources'] as const).map((v) => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1 rounded-md text-sm ${view === v ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
                {v === 'teams' ? 'Companies / teams' : v === 'users' ? 'Users' : 'Features & jobs'}
              </button>
            ))}
            <button onClick={() => load(days)} className="ml-auto text-sm text-indigo-600 hover:text-indigo-800">Refresh</button>
          </div>

          {loading && <p className="text-sm text-gray-500">Reading the query log…</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}

          {data && !loading && (
            <>
              <div className="flex flex-wrap gap-6 mb-4 text-sm text-gray-700">
                <span><strong className="tabular-nums">{cpu(data.total.cpuSeconds)}</strong> CPU time in total</span>
                <span><strong className="tabular-nums">{n(data.total.queries)}</strong> queries</span>
                <span><strong className="tabular-nums">{data.total.readGB.toFixed(1)} GB</strong> read</span>
              </div>

              <div className="overflow-x-auto">
                {view === 'teams' && (
                  <table className="min-w-full">
                    <thead className="bg-gray-50"><tr><th className={th}>Company / team</th><th className={th}>Users</th>{numHeads}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.teams.map((t) => (
                        <tr key={t.teamId ?? `solo-${t.members[0]}`}>
                          <td className={td}>{t.teamName || (
                            <span className="text-gray-500">No team · {data.users.find((u) => u.userId === t.members[0])?.email}</span>)}</td>
                          <td className={td}>{t.members.length}</td>
                          {numCells(t)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {view === 'users' && (
                  <table className="min-w-full">
                    <thead className="bg-gray-50"><tr><th className={th}>User</th><th className={th}>Team</th>{numHeads}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.users.map((u) => (
                        <React.Fragment key={u.userId}>
                          <tr className="cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(expanded === u.userId ? null : u.userId)}>
                            <td className={td}>
                              <div>{u.name || u.email}</div>
                              {u.name && <div className="text-xs text-gray-500">{u.email}</div>}
                            </td>
                            <td className={td}>{u.teamName || '—'}</td>
                            {numCells(u)}
                          </tr>
                          {expanded === u.userId && (
                            <tr><td colSpan={6} className="px-6 pb-3 bg-gray-50">
                              <div className="text-xs text-gray-500 py-2">What used it</div>
                              {u.topRoutes.map((r) => (
                                <div key={r.route} className="flex flex-wrap justify-between gap-2 text-xs py-1 border-t border-gray-100">
                                  <code className="text-gray-700 break-all">{r.route}</code>
                                  <span className="tabular-nums text-gray-600">{cpu(r.cpuSeconds)} · {n(r.queries)} queries · {r.share.toFixed(1)}%</span>
                                </div>
                              ))}
                            </td></tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}

                {view === 'sources' && (
                  <table className="min-w-full">
                    <thead className="bg-gray-50"><tr><th className={th}>Feature / job</th>{numHeads}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.sources.map((s) => (
                        <tr key={s.source}><td className={td}><code className="text-xs break-all">{s.source}</code></td>{numCells(s)}</tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <p className="mt-4 text-xs text-gray-500">
                Share = share of query CPU time across all database replicas. The bill is for the database running, not per query,
                so this is the fair split rather than an exact cost. Background merges aren't included. "untagged" = queries from
                before this tracking went live, the SQL console and other tools.
                {data.oldestLogDay && <> The log goes back to {data.oldestLogDay}.</>}
                {!data.allReplicas && <> Showing one replica only{data.note ? ` — ${data.note}` : ''}.</>}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ComputeUsagePanel;
